/**
 * @fileoverview Funções utilitárias de baixo nível para formatação e parsing
 * @module Utils
 * @version 1.0.0
 */

import {
  parse as dfParse,
  format as dfFormat,
  isValid,
  differenceInDays,
  startOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import type {
  FormatOptions,
  ParseOptions,
  ProtheusError,
  FormattableValue,
  ParseableValue,
  ParseResult,
} from "./types";

/**
 * Função interna genérica para aplicar formatação baseada em categoria
 *
 * @internal
 * @param value - Valor a ser formatado
 * @param options - Opções de formatação
 * @returns String formatada ou fallback em caso de erro
 *
 * @example
 * ```ts
 * formatInternal("20250528", { category: "date:BR", fallback: "" });
 * // Returns: "28/05/2025"
 * ```
 */
export function formatInternal(
  value: FormattableValue,
  options: FormatOptions
): string {
  const { category, decimals = 2, maxInitials = 2, fallback = "" } = options;

  if (value == null) {
    return fallback;
  }

  try {
    if (category.startsWith("date:")) {
      return handleDateFormatting(value, category, fallback);
    }

    return handleNonDateFormatting(
      value,
      category,
      decimals,
      maxInitials,
      fallback
    );
  } catch (error) {
    console.warn("Erro na formatação:", error);
    return fallback;
  }
}

/**
 * Manipula formatação de datas baseada na categoria
 *
 * @internal
 */
function handleDateFormatting(
  value: FormattableValue,
  category: string,
  fallback: string
): string {
  if (category === "date:BR" || category === "date:CELL") {
    const date = parseToDate(value);
    return date && isValid(date)
      ? dfFormat(date, "dd/MM/yyyy", { locale: ptBR })
      : fallback;
  }

  if (category === "date:YYYYMMDD") {
    const date = parseToDate(value);
    return date && isValid(date) ? dfFormat(date, "yyyyMMdd") : fallback;
  }

  console.log(`Formato de data não suportado: ${category}`);
  return fallback;
}

/**
 * Manipula formatação de valores não relacionados a datas
 *
 * @internal
 */
function handleNonDateFormatting(
  value: FormattableValue,
  category: string,
  decimals: number,
  maxInitials: number,
  fallback: string
): string {
  const stringValue = String(value);

  switch (category) {
    case "currency":
      return formatCurrency(value, decimals);

    case "percentage":
      return formatPercentage(value, decimals);

    case "cnpj":
      return formatCNPJ(stringValue, fallback);

    case "capitalize":
      return capitalizeString(stringValue);

    case "initials":
      return generateInitials(stringValue, maxInitials, fallback);

    default:
      console.log(`Categoria de formatação não suportada: ${category}`);
      return fallback;
  }
}

/**
 * Converte valor para Date, suportando múltiplos formatos
 *
 * @internal
 */
function parseToDate(value: FormattableValue): Date | null {
  if (value instanceof Date) {
    return value;
  }

  const rawValue = String(value).trim();

  // Formato YYYYMMDD (8 dígitos)
  if (/^\d{8}$/.test(rawValue)) {
    return dfParse(rawValue, "yyyyMMdd", new Date());
  }

  // Outros formatos (ISO, timestamp, etc.)
  const date = new Date(rawValue);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formata valor numérico como moeda brasileira
 *
 * @internal
 */
function formatCurrency(value: FormattableValue, decimals: number): string {
  const numericValue = Number(value) || 0;

  // Detecta quantas casas decimais o número realmente tem
  const actualDecimals = (numericValue.toString().split(".")[1] || "").length;

  // Usa o máximo entre as casas decimais reais e o valor mínimo especificado
  const finalDecimals = Math.max(actualDecimals, decimals);

  return numericValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: finalDecimals,
    maximumFractionDigits: finalDecimals,
  });
}

/**
 * Formata valor numérico como porcentagem
 *
 * @internal
 */
function formatPercentage(value: FormattableValue, decimals: number): string {
  const numericValue = Number(value) || 0;
  return `${numericValue.toFixed(decimals)}%`;
}

/**
 * Formata CNPJ aplicando máscara
 *
 * @internal
 */
function formatCNPJ(value: string, fallback: string): string {
  const digits = value.replace(/\D/g, "");

  if (digits.length !== 14) {
    return fallback;
  }

  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
}

/**
 * Capitaliza cada palavra de uma string
 *
 * @internal
 */
function capitalizeString(value: string): string {
  return value
    .trim()
    .split(/[._\-\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Gera iniciais a partir de uma string
 *
 * @internal
 */
function generateInitials(
  value: string,
  maxInitials: number,
  fallback: string
): string {
  const initials = value
    .trim()
    .split(/[._\-\s]+/)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, maxInitials)
    .join("");

  return initials || fallback;
}

/**
 * Função genérica para parsing baseado em categoria
 *
 * @internal
 * @param value - Valor a ser parseado
 * @param options - Opções de parsing
 * @returns Valor parseado ou null/0 baseado em allowNull
 */
export function parseInternal(
  value: ParseableValue,
  options: ParseOptions
): ParseResult<Date | number | ProtheusError> {
  const { category, timePart, allowNull = false } = options;

  if (value == null) {
    return allowNull ? null : getDefaultValue(category);
  }

  try {
    if (category.startsWith("date:")) {
      return handleDateParsing(value, category, timePart);
    }

    return handleNonDateParsing(value, category, allowNull);
  } catch (error) {
    console.warn("Erro no parsing:", error);
    return allowNull ? null : getDefaultValue(category);
  }
}

/**
 * Retorna valor padrão baseado na categoria
 *
 * @internal
 */
function getDefaultValue(category: string): Date | number | ProtheusError {
  if (category.startsWith("date:")) {
    return new Date();
  }

  if (category === "protheus:error") {
    return {
      code: "",
      nf: undefined,
      serie: undefined,
      fornecedor: undefined,
      loja: undefined,
      emissao: "",
    };
  }

  return 0;
}

/**
 * Manipula parsing de datas
 *
 * @internal
 */
function handleDateParsing(
  value: string,
  category: string,
  timePart?: string
): Date | null {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  switch (category) {
    case "date:BR":
      return parseBRDate(trimmedValue);

    case "date:YYYYMMDD":
      return parseYYYYMMDDDate(trimmedValue, timePart);

    case "date:PROTHEUS":
      // Implementar lógica específica do Protheus se necessário
      return null;

    default:
      console.log(`Formato de data não suportado: ${category}`);
      return null;
  }
}

/**
 * Parse de data no formato brasileiro (dd/MM/yyyy)
 *
 * @internal
 */
function parseBRDate(value: string): Date | null {
  const baseDateString = value.split(" ")[0];
  const date = dfParse(baseDateString, "dd/MM/yyyy", new Date());
  return isValid(date) ? date : null;
}

/**
 * Parse de data no formato YYYYMMDD
 *
 * @internal
 */
function parseYYYYMMDDDate(value: string, timePart?: string): Date | null {
  const datePart = value.substring(0, 8);
  const date = dfParse(datePart, "yyyyMMdd", new Date());

  if (!isValid(date)) {
    return null;
  }

  if (timePart && /^\d{6}$/.test(timePart)) {
    const timeMatches = timePart.match(/.{2}/g);
    if (timeMatches) {
      const [hours, minutes, seconds] = timeMatches;
      date.setUTCHours(+hours, +minutes, +seconds, 0);
    }
  }

  return date;
}

/**
 * Manipula parsing de valores não relacionados a datas
 *
 * @internal
 */
function handleNonDateParsing(
  value: string,
  category: string,
  allowNull: boolean
): number | ProtheusError | null {
  const stringValue = String(value);

  switch (category) {
    case "float":
      return parseFloat(stringValue, allowNull);

    case "digits":
      return parseDigits(stringValue, allowNull);

    case "protheus:error":
      return parseProtheusError(stringValue);

    default:
      console.log(`Categoria de parsing não suportada: ${category}`);
      return null;
  }
}

/**
 * Parse de número decimal com formato brasileiro
 *
 * @internal
 */
function parseFloat(value: string, allowNull: boolean): number | null {
  let cleanedValue = value.replace(/R?\$\s?/g, "").trim();

  if (cleanedValue.includes(",")) {
    cleanedValue = cleanedValue.replace(/\./g, "").replace(/,/g, ".");
  }

  const floatValue = Number.parseFloat(cleanedValue);
  return Number.isNaN(floatValue) ? (allowNull ? null : 0) : floatValue;
}

/**
 * Extrai dígitos de uma string
 *
 * @internal
 */
function parseDigits(value: string, allowNull: boolean): number | null {
  const digits = value.replace(/\D/g, "");
  const integerValue = Number.parseInt(digits, 10);
  return Number.isNaN(integerValue) ? (allowNull ? null : 0) : integerValue;
}

/**
 * Parse de string de erro do Protheus
 *
 * @internal
 */
function parseProtheusError(value: string): ProtheusError {
  const defaultError: ProtheusError = {
    code: "",
    nf: undefined,
    serie: undefined,
    fornecedor: undefined,
    loja: undefined,
    emissao: "",
  };

  if (!value) {
    return defaultError;
  }

  const cleanValue = value.replace(/\s+/g, " ");
  const pairs = [
    ...cleanValue.matchAll(/-\s*(\w+)\s+([^\s-]+(?:\s+[^\s-]+)*?)(?=\s*-|$)/g),
  ];

  const keyValueMap: Record<string, string> = Object.fromEntries(
    pairs.map(([, key, val]) => [key.trim(), val.trim()])
  );

  const formattedDate = formatProtheusDate(
    keyValueMap.dDEmissao || keyValueMap.dDemissao
  );

  return {
    code: cleanValue.match(/AJUDA\w+/)?.[0] || keyValueMap.AJUDACODE || "",
    nf: keyValueMap.cNFiscal,
    serie: keyValueMap.cSerie,
    fornecedor: keyValueMap.cA100For,
    loja: keyValueMap.cLoja,
    emissao: formattedDate,
  };
}

/**
 * Formata data do Protheus (DDMMYYYY para DD/MM/YYYY)
 *
 * @internal
 */
function formatProtheusDate(dateString?: string): string {
  if (!dateString) {
    return "";
  }

  return dateString.replace(
    /(\d{2})(\d{2})(\d{4})/,
    (_, day, month, year) => `${day}/${month}/${year}`
  );
}

/**
 * Calcula diferença em dias entre duas datas
 *
 * @param endDate - Data final
 * @param startDate - Data inicial (padrão: hoje)
 * @returns Diferença em dias
 *
 * @public
 */
export function calculateDateDifference(
  endDate: Date | null | undefined,
  startDate: Date | null | undefined = new Date()
): number {
  if (!endDate || !startDate) {
    return 0;
  }

  const normalizedEndDate = startOfDay(endDate);
  const normalizedStartDate = startOfDay(startDate);

  return differenceInDays(normalizedEndDate, normalizedStartDate);
}

/**
 * Combina classes CSS usando clsx e twMerge para resolver conflitos do Tailwind
 *
 * @param inputs - Lista de valores de classe CSS
 * @returns String com classes CSS combinadas e conflitos resolvidos
 *
 * @example
 * ```ts
 * cn("text-red-500", "text-blue-500"); // "text-blue-500"
 * cn("px-4", "px-6", "py-2"); // "px-6 py-2"
 * ```
 *
 * @public
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
